import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import { create } from 'zustand';

export const ONBOARDING_KEY = 'onboarding_complete';
const SIGNUP_DRAFT_KEY = 'signup_draft';
const PROFILE_SETUP_DRAFT_KEY = 'profile_setup_draft';
const PROFILE_SETUP_PENDING_USER_ID_KEY = 'profile_setup_pending_user_id';

export type SignupDraftStep = 'credentials' | 'profile';

export type SignupDraft = {
  email: string;
  username: string;
  displayName: string;
  step: SignupDraftStep;
};

export type ProfileSetupDraft = {
  username: string;
  displayName: string;
  bio: string;
  avatarUrl: string;
  interestTags: string[];
};

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

async function readStoredValue(key: string): Promise<string | null> {
  try {
    if (Platform.OS === 'web') {
      return localStorage.getItem(key);
    }
    return await SecureStore.getItemAsync(key);
  } catch {
    return null;
  }
}

async function writeStoredValue(key: string, value: string): Promise<void> {
  try {
    if (Platform.OS === 'web') {
      localStorage.setItem(key, value);
      return;
    }
    await SecureStore.setItemAsync(key, value);
  } catch {
    // best-effort
  }
}

async function deleteStoredValue(key: string): Promise<void> {
  try {
    if (Platform.OS === 'web') {
      localStorage.removeItem(key);
      return;
    }
    await SecureStore.deleteItemAsync(key);
  } catch {
    // best-effort
  }
}

async function readStoredJSON<T>(key: string): Promise<T | null> {
  const rawValue = await readStoredValue(key);
  if (!rawValue) {
    return null;
  }
  try {
    return JSON.parse(rawValue) as T;
  } catch {
    return null;
  }
}

async function readOnboardingFlag(): Promise<boolean> {
  const value = await readStoredValue(ONBOARDING_KEY);
  return value === 'true';
}

async function writeOnboardingFlag(): Promise<void> {
  await writeStoredValue(ONBOARDING_KEY, 'true');
}

export async function readSignupDraft(): Promise<SignupDraft | null> {
  const draft = await readStoredJSON<Partial<SignupDraft>>(SIGNUP_DRAFT_KEY);
  if (!draft) {
    return null;
  }
  return {
    email: typeof draft.email === 'string' ? draft.email : '',
    username: typeof draft.username === 'string' ? draft.username : '',
    displayName: typeof draft.displayName === 'string' ? draft.displayName : '',
    step: draft.step === 'profile' ? 'profile' : 'credentials',
  };
}

export async function writeSignupDraft(draft: SignupDraft): Promise<void> {
  await writeStoredValue(SIGNUP_DRAFT_KEY, JSON.stringify(draft));
}

export async function clearSignupDraft(): Promise<void> {
  await deleteStoredValue(SIGNUP_DRAFT_KEY);
}

export async function readProfileSetupDraft(): Promise<ProfileSetupDraft | null> {
  const draft = await readStoredJSON<Partial<ProfileSetupDraft>>(PROFILE_SETUP_DRAFT_KEY);
  if (!draft) {
    return null;
  }
  return {
    username: typeof draft.username === 'string' ? draft.username : '',
    displayName: typeof draft.displayName === 'string' ? draft.displayName : '',
    bio: typeof draft.bio === 'string' ? draft.bio : '',
    avatarUrl: typeof draft.avatarUrl === 'string' ? draft.avatarUrl : '',
    interestTags: Array.isArray(draft.interestTags)
      ? draft.interestTags.filter((value): value is string => typeof value === 'string')
      : [],
  };
}

export async function writeProfileSetupDraft(draft: ProfileSetupDraft): Promise<void> {
  await writeStoredValue(PROFILE_SETUP_DRAFT_KEY, JSON.stringify(draft));
}

export async function clearProfileSetupDraft(): Promise<void> {
  await deleteStoredValue(PROFILE_SETUP_DRAFT_KEY);
}

// ---------------------------------------------------------------------------
// Store — shared between _layout.tsx and onboarding.tsx
// ---------------------------------------------------------------------------

type OnboardingStore = {
  /** null = not yet read from storage */
  isComplete: boolean | null;
  profileSetupPendingUserID: string | null;
  hydrate: () => Promise<void>;
  complete: () => Promise<void>;
  markProfileSetupPending: (userID: string) => Promise<void>;
  clearProfileSetupPending: () => Promise<void>;
};

export const useOnboarding = create<OnboardingStore>((set, get) => ({
  isComplete: null,
  profileSetupPendingUserID: null,

  async hydrate() {
    if (get().isComplete !== null) {
      return;
    }
    const [value, profileSetupPendingUserID] = await Promise.all([
      readOnboardingFlag(),
      readStoredValue(PROFILE_SETUP_PENDING_USER_ID_KEY),
    ]);
    set({ isComplete: value, profileSetupPendingUserID });
  },

  async complete() {
    await writeOnboardingFlag();
    set({ isComplete: true });
  },

  async markProfileSetupPending(userID) {
    await writeStoredValue(PROFILE_SETUP_PENDING_USER_ID_KEY, userID);
    set({ profileSetupPendingUserID: userID });
  },

  async clearProfileSetupPending() {
    await deleteStoredValue(PROFILE_SETUP_PENDING_USER_ID_KEY);
    set({ profileSetupPendingUserID: null });
  },
}));
